import { CalendarCheck, Trophy, TrendingUp, Eye, MousePointerClick, Percent, Globe } from "lucide-react";
import { fmt } from "./StoryParts";
import { delta, monthLabel, rangeLabel } from "./trendData";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={testId}>
    <div className="flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5 text-lime-300" /><p className="text-[10px] uppercase tracking-[0.14em] text-lime-200/80 font-bold">{label}</p></div>
    <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">{value}</p>
  </div>
);

const GrowthLine = ({ label, d, text, testId }) => {
  if (!d) return null;
  const up = d.dir === "up";
  const cls = d.dir === "flat" ? "text-slate-200" : up ? "text-lime-300" : "text-amber-300";
  return (
    <p className="text-slate-200 text-sm" data-testid={testId}>
      <TrendingUp className={`inline w-4 h-4 mr-1 -mt-0.5 ${cls}`} />
      {label}: <span className={`font-semibold ${cls}`}>{d.dir === "flat" ? "held steady" : `${up ? "up" : "down"} ${Math.abs(d.pct).toFixed(0)}%`}</span> {text}
      {d.dir === "down" && <span className="text-amber-300/90"> Worth a conversation.</span>}
    </p>
  );
};

export default function YearInReview({ yir, clientName, channelLabel, typicalLabel = "ad", benchmark, p }) {
  const { year, total, best, early, late, lastYear } = yir;
  const mult = benchmark > 0 ? total.ctr / benchmark : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl p-5 md:p-6 mb-6 border border-lime-400/30 bg-gradient-to-br from-lime-400/10 via-sky-500/10 to-indigo-500/10" data-testid={`${p}-year-in-review`}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarCheck className="w-5 h-5 text-lime-300" />
        <p className="text-lime-300 text-xs font-bold uppercase tracking-[0.18em]">{year} Year in Review</p>
      </div>
      <p className="text-slate-100 text-base md:text-lg leading-relaxed mb-5" data-testid={`${p}-yir-story`}>
        In {year}, {clientName}&apos;s {channelLabel} ads were seen <span className="text-white font-bold">{fmt(total.impressions)} times</span> and
        earned <span className="text-white font-bold">{fmt(total.clicks)} clicks</span> across {total.count} months
        {mult >= 1 ? <>, a {total.ctr.toFixed(2)}% click rate that ran <span className="text-lime-300 font-bold">{mult.toFixed(1)}x the typical</span> {typicalLabel}</> : null}.
        {" "}<span className="text-white font-semibold">{monthLabel(best.month, true)}</span> was the standout month.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat icon={Eye} label="Views this year" value={fmt(total.impressions)} testId={`${p}-yir-impressions`} />
        <Stat icon={MousePointerClick} label="Clicks this year" value={fmt(total.clicks)} testId={`${p}-yir-clicks`} />
        <Stat icon={Percent} label="Click rate" value={`${total.ctr.toFixed(2)}%`} testId={`${p}-yir-ctr`} />
        {total.view_through > 0
          ? <Stat icon={Globe} label="Bonus visits, no click" value={fmt(total.view_through)} testId={`${p}-yir-bonus`} />
          : <Stat icon={Trophy} label="Best month clicks" value={fmt(best.clicks)} testId={`${p}-yir-best-clicks`} />}
      </div>
      <div className="space-y-1.5">
        <GrowthLine label="Clicks per month" d={delta(yir.clicksPerMonth.late, yir.clicksPerMonth.early)} text={`from ${rangeLabel(early.keys)} to ${rangeLabel(late.keys)}.`} testId={`${p}-yir-growth-clicks`} />
        <GrowthLine label="Click rate" d={delta(late.ctr, early.ctr)} text={`(${early.ctr.toFixed(2)}% early in the year, ${late.ctr.toFixed(2)}% by year end).`} testId={`${p}-yir-growth-ctr`} />
        {total.back_impressions > 0 && (
          <GrowthLine label="Returning people" d={delta(late.back_impressions, early.back_impressions)} text="as more people got to know the brand." testId={`${p}-yir-growth-returning`} />
        )}
        {lastYear && <GrowthLine label={`Clicks vs ${+year - 1}`} d={delta(total.clicks, lastYear.clicks)} text={`(${fmt(lastYear.clicks)} last year).`} testId={`${p}-yir-vs-last-year`} />}
      </div>
    </div>
  );
}
